/**
 * ATL-1694 — the pure half of the AI-assistant screens (consent + help): which translation key
 * explains a scope, an availability reason, a refusal, and which client the person is trusting.
 *
 * i18next reads `.` as nesting and `:` as a namespace separator, so a scope such as `read:sales`
 * is looked up as `aiAssistants.consentScope_read_sales`.
 */
import type { AiAssistantsInfo, AiAvailability, AiClient, ConnectRequest } from './aiAssistantsApi'

const keyPart = (value: string): string => value.replace(/[.:]/g, '_')

/** The plain-language explanation of what a scope lets the assistant do, for the consent screen. */
export const consentScopeKey = (scope: string): string => `aiAssistants.consentScope_${keyPart(scope)}`

/**
 * Why this organization cannot connect an assistant, as a translation key, or `null` when it can.
 * An unknown reason reads as the generic "not enabled yet" rather than as available.
 */
export const availabilityMessageKey = (availability: AiAvailability | null | undefined): string | null => {
  if (!availability || availability.available) return null
  switch (availability.reason) {
    case 'organization_not_on_supabase':
      return 'aiAssistants.unavailable_organization_not_on_supabase'
    case 'organization_access_undetermined':
    case 'mcp_access_undetermined':
      return 'aiAssistants.unavailable_undetermined'
    case 'mcp_disabled':
    case 'mcp_not_enabled_for_organization':
    default:
      return 'aiAssistants.unavailable_not_enabled'
  }
}

/**
 * Who is asking, as the consent screen shows it. The HOST is what the authorization code is sent
 * to, so it is the identity shown prominently; the client's own name is self-asserted and shown
 * second, if at all.
 */
export const clientTitle = (client: AiClient): { key: string; host: string } => {
  switch (client.kind) {
    case 'claude':
      return { key: 'aiAssistants.client_claude', host: client.host }
    case 'chatgpt':
      return { key: 'aiAssistants.client_chatgpt', host: client.host }
    case 'local':
      return { key: 'aiAssistants.client_local', host: client.host }
    default:
      return { key: 'aiAssistants.client_other', host: client.host }
  }
}

/** The connection label in the API tokens list ("Connected through Claude"). */
export const connectionLabelKey = (client: AiClient | null | undefined): string =>
  client ? `aiAssistants.connectedThrough_${client.kind}` : 'aiAssistants.connectedThrough_other'

export type ConsentBlocker =
  | { kind: 'not_admin' }
  | { kind: 'not_found' }
  | { kind: 'stale' }
  | { kind: 'unavailable'; key: string }

/**
 * Whether the loaded request can be decided at all, and if not, which message replaces the form.
 * Order matters: a request that is no longer pending is stale whatever else is true of it.
 */
export const consentBlocker = (request: ConnectRequest, now: number = Date.now()): ConsentBlocker | null => {
  if (request.status !== 'pending') return { kind: 'stale' }
  const expiresAt = Date.parse(request.expiresAt)
  if (Number.isFinite(expiresAt) && expiresAt <= now) return { kind: 'stale' }
  const key = availabilityMessageKey(request.availability)
  if (key) return { kind: 'unavailable', key }
  return null
}

/** A failed load of the request, as the blocker that replaces the form, or `null` for "retry". */
export const loadFailureBlocker = (status: number | null): ConsentBlocker | null => {
  if (status === 403) return { kind: 'not_admin' }
  if (status === 404) return { kind: 'not_found' }
  if (status === 409 || status === 410) return { kind: 'stale' }
  return null
}

/** Refusal codes the consent screen explains in its own words; anything else is the generic error. */
const DECISION_ERROR_CODES = [
  'request_not_pending',
  'request_expired',
  'mcp_not_enabled_for_organization',
  'mcp_disabled',
  'organization_not_on_supabase',
  'too_many_active_tokens',
  'invalid_scopes',
  'invalid_expiry',
  // 503: the backend could not check the organization's status — try again, not "no".
  'mcp_access_undetermined',
  'organization_access_undetermined',
  'forbidden',
] as const

export const decisionErrorKey = (code: string | null, status: number | null): string => {
  if (code && (DECISION_ERROR_CODES as readonly string[]).includes(code)) return `aiAssistants.decisionError_${code}`
  if (status === 403) return 'aiAssistants.decisionError_forbidden'
  if (status === null) return 'aiAssistants.networkError'
  return 'aiAssistants.genericError'
}

export const DECISION_ERROR_CODE_LIST: readonly string[] = DECISION_ERROR_CODES

/** The expiry choice to preselect: the backend's default when it is allowed, else the first allowed. */
export const initialExpiryDays = (request: Pick<ConnectRequest, 'defaultExpiresInDays' | 'allowedExpiresInDays'>): number | null => {
  if (request.allowedExpiresInDays.includes(request.defaultExpiresInDays)) return request.defaultExpiresInDays
  return request.allowedExpiresInDays[0] ?? null
}

/** Scopes preselected on the consent screen: every scope the assistant asked for. */
export const initialScopes = (request: Pick<ConnectRequest, 'requestedScopes'>): string[] => [...request.requestedScopes]

/** Example prompts on the help screen, in order. Each is a translation key. */
export const EXAMPLE_PROMPT_KEYS = [
  'aiAssistants.example_revenue',
  'aiAssistants.example_topProducts',
  'aiAssistants.example_stock',
  'aiAssistants.example_salesToday',
  'aiAssistants.example_acceptanceDraft',
] as const

/**
 * Whether Settings offers the AI-assistant guide at all: only when the server says this
 * organization can connect an assistant NOW. The server ships dark (`mcp_disabled`) and admits
 * organizations by allowlist, so a guide shown on any other answer hands people a server address
 * that refuses them. A temporary "could not check" hides it too; the next visit asks again.
 */
export const showsAiAssistantsEntry = (info: Pick<AiAssistantsInfo, 'availability'> | null | undefined): boolean =>
  info?.availability.available === true

/**
 * Whether a failed `/ai-assistants/info` is worth a Sentry event. No response (offline) is not; a
 * 404 is a backend from before ATL-1694 (rolled back, or not deployed yet), i.e. "not available".
 */
export const reportsInfoFailure = (status: number | null): boolean => status !== null && status !== 404

/** The command that connects Claude Code to this server. */
export const claudeCodeCommand = (serverUrl: string): string => `claude mcp add --transport http atlas ${serverUrl}`
